// Utility to handle navigation and reloads properly
// Prevents infinite reload loops and uses React Router when possible

import { NavigateFunction } from 'react-router-dom';

/**
 * Safe reload function that prevents infinite loops
 * Checks if we've already reloaded recently to avoid reload loops
 */
export const safeReload = () => {
  const lastReloadTime = sessionStorage.getItem('last-reload-time');
  const now = Date.now();
  
  // If we reloaded less than 3 seconds ago, skip
  if (lastReloadTime && (now - parseInt(lastReloadTime)) < 3000) {
    console.warn('⚠️ Skipping reload - already reloaded recently');
    return;
  }
  
  sessionStorage.setItem('last-reload-time', now.toString());
  window.location.reload();
};

/**
 * Preferred method: Use React Router navigation instead of reload
 * @param navigate - React Router navigate function
 * @param path - Path to navigate to
 * @param replace - Whether to replace current history entry
 */
export const navigateWithRefresh = (
  navigate: NavigateFunction,
  path: string,
  replace: boolean = false
) => {
  navigate(path, { replace, state: { forceRefresh: Date.now() } });
};

/**
 * Clear all caches and reload (useful for logout or major state changes)
 */
export const clearCachesAndReload = async () => {
  try {
    // Clear all caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    }
    
    // Clear session storage (but keep last reload time)
    const lastReload = sessionStorage.getItem('last-reload-time');
    sessionStorage.clear();
    if (lastReload) {
      sessionStorage.setItem('last-reload-time', lastReload);
    }
    
    // Unregister service worker
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(reg => reg.unregister()));
    }
    
    safeReload();
  } catch (error) {
    console.error('Error clearing caches:', error);
    safeReload();
  }
};

/**
 * Check if we're in an infinite reload loop
 * @returns true if in a reload loop
 */
export const isInReloadLoop = (): boolean => {
  const reloadCount = parseInt(sessionStorage.getItem('reload-count') || '0');
  const resetTime = parseInt(sessionStorage.getItem('reload-reset-time') || '0');
  const now = Date.now();
  
  // Reset counter every 10 seconds
  if (now - resetTime > 10000) {
    sessionStorage.setItem('reload-count', '0');
    sessionStorage.setItem('reload-reset-time', now.toString());
    return false;
  }
  
  // Increment counter
  sessionStorage.setItem('reload-count', (reloadCount + 1).toString());
  
  // If more than 3 reloads in 10 seconds, we're in a loop
  if (reloadCount > 3) {
    console.error('🔴 RELOAD LOOP DETECTED! Stopping automatic reloads.');
    return true;
  }
  
  return false;
};
